// 숙련 창(캐릭터 창 「숙련」 탭)과 대장간 초월 구역 DOM(C4). 값·문구·배치는 masteryModel.ts(순수), 변경은 GameCommand로만 보낸다.
// RpgUi 창 본문 안에만 그린다(플레이 화면 위 팝업 없음, alert/confirm 금지 — 재분배 확인은 화면 안 확인 상자). 값이 바뀔 때만 다시 그린다(RpgUi가 key()·version으로 판단).
// 숙련 레벨업·이정표 받기에는 짧은 연출(테 빛 한 번, 효과음은 기존 levelUp·skillLearn·uiConfirm 재사용). 움직임 줄이기면 빛 애니메이션 없이 문구만.
import type { EquipSlot } from '../shared/content/types';
import type { GameCommand, GameView } from '../shared/types';
import type { MasteryAlloc } from '../shared/mastery';
import { FONT, FONT_NUM, el } from './dom';
import { makeRequestId, type Tone } from './rpgModel';
import { uiSfx } from './clearFx';
import { reducedMotion } from './skillbar';
import { BTN, draftStep, masteryMessage, masteryModel, statRowLayout, transcendLayout, transcendMessage, transcendModel, TRANSCEND_RULE } from './masteryModel';

export const MASTERY_TAB_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="m12 2 2.6 6.2 6.4.6-4.9 4.3 1.5 6.4L12 16l-5.6 3.5 1.5-6.4L3 9.2l6.4-.6z"/></svg>';

const STYLE_ID = 'dh-mastery-style';
const TONE: Record<Tone, string> = { ok: '#7dffa0', warn: '#ffe7a0', err: '#ff7a86' };
const CSS =
  '.dh-m{display:flex;flex-direction:column;gap:10px;font-family:' + FONT + ';color:#eef2ff;grid-column:1/-1}' +
  '.dh-m-h{display:flex;justify-content:space-between;align-items:baseline;gap:8px;font:900 15px ' + FONT + ';color:#ffd76a}' +
  '.dh-m-h small{font:700 12px ' + FONT + ';color:#aeb9da}' +
  '.dh-m-card{border-radius:10px;border:1.5px solid rgba(242,193,78,.4);background:linear-gradient(180deg,rgba(32,43,78,.95),rgba(12,17,34,.95));padding:10px 12px;display:flex;flex-direction:column;gap:6px}' +
  '.dh-m-lv{font:900 34px ' + FONT_NUM + ';color:#fff;text-shadow:0 2px 6px rgba(0,0,0,.7);line-height:1}' +
  '.dh-m-bar{position:relative;height:12px;border-radius:6px;background:rgba(255,255,255,.08);border:1px solid rgba(242,193,78,.5);overflow:hidden}' +
  '.dh-m-bar>span{position:absolute;left:0;top:0;bottom:0;background:linear-gradient(90deg,#7fd8ff,#b48cff)}' +
  '.dh-m-note{font:700 11.5px ' + FONT + ';color:#aeb9da;line-height:1.4}' +
  '.dh-m-warn{font:800 12px ' + FONT + ';color:#ffb37a;line-height:1.4}' +
  '.dh-m-row{display:grid;align-items:center;gap:8px;padding:5px 10px;border-radius:10px;border:1.5px solid rgba(242,193,78,.35);background:rgba(14,20,42,.9)}' +
  '.dh-m-row b{display:block;font:800 15px ' + FONT + ';color:#f1f3f9}' +
  '.dh-m-row small{display:block;font:600 12px ' + FONT + ';color:#abbcd3}' +
  '.dh-m-val{text-align:center;font:900 18px ' + FONT + ';color:#ffe07a}' +
  '.dh-m-btn{min-width:var(--dh-btn-min,' + BTN + 'px);min-height:var(--dh-btn-min,' + BTN + 'px);box-sizing:border-box;padding:4px 12px;border-radius:8px;border:2px solid #c8963e;background:linear-gradient(180deg,#2a3560,#151c38);color:#ffe07a;font:900 15px ' + FONT + ';cursor:pointer}' +
  '.dh-m-btn.sq{padding:0;font-size:20px}' +
  '.dh-m-btn.pri{background:linear-gradient(180deg,#f2c14e,#c8963e);color:#1a1206;border-color:#ffe7a0}' +
  '.dh-m-btn:disabled{opacity:.4;cursor:not-allowed}' +
  '.dh-m-ms{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:8px;padding:6px 10px;border-radius:8px;border:1.5px solid rgba(242,193,78,.25);background:rgba(14,20,42,.8)}' +
  '.dh-m-ms.ready{border-color:#7fd8ff;box-shadow:0 0 10px rgba(127,216,255,.35)}' +
  '.dh-m-ms.claimed{opacity:.55}' +
  '.dh-m-ms b{font:800 13px ' + FONT + '}.dh-m-ms small{display:block;font:600 11.5px ' + FONT + ';color:#abbcd3}' +
  '.dh-m-un{display:flex;flex-wrap:wrap;gap:6px}' +
  '.dh-m-un span{padding:3px 10px;border-radius:14px;border:1.5px solid rgba(174,185,218,.35);font:800 12px ' + FONT + ';color:#8f9bbd}' +
  '.dh-m-un span.ok{border-color:#7fd8ff;color:#bfeaff;background:rgba(127,216,255,.12)}' +
  '.dh-m-confirm{border-radius:10px;border:2px solid #ffb37a;background:rgba(60,28,12,.9);padding:10px 12px;display:flex;flex-direction:column;gap:8px}' +
  '.dh-m-grid{display:grid;gap:8px}' +
  '.dh-m-tc{border-radius:10px;border:2px solid rgba(180,140,255,.55);background:linear-gradient(180deg,rgba(40,34,84,.95),rgba(14,14,36,.95));padding:10px;display:flex;flex-direction:column;gap:4px;box-sizing:border-box;min-width:0}' +
  '.dh-m-tc.s1{border-color:#6fd6ff}.dh-m-tc.s2{border-color:#a78bff}.dh-m-tc.s3{border-color:#ff6fd8}.dh-m-tc.s4{border-color:#ffd36a}.dh-m-tc.s5{border-color:#fff6e0;box-shadow:0 0 12px rgba(255,246,224,.4)}' +
  '.dh-m-tc .nm{display:flex;justify-content:space-between;align-items:baseline;font:900 15px ' + FONT + '}' +
  '.dh-m-tc .nm i{font-style:normal;font:900 18px ' + FONT_NUM + ';color:#ffe07a}' +
  '.dh-m-tc .ch{font:900 26px ' + FONT_NUM + ';color:#fff;text-shadow:0 2px 6px rgba(0,0,0,.7)}' +
  '.dh-m-seg{display:flex;gap:6px}' +
  '.dh-m-seg .dh-m-btn{flex:1 1 0}' +
  '.dh-m-seg .dh-m-btn[aria-pressed=true]{background:linear-gradient(180deg,#f2c14e,#c8963e);color:#1a1206;border-color:#ffe7a0}' +
  '@keyframes dhMsGlow{0%{box-shadow:0 0 0 0 rgba(127,216,255,0)}25%{box-shadow:0 0 18px 4px rgba(127,216,255,.65)}100%{box-shadow:0 0 0 0 rgba(127,216,255,0)}}' +
  '.dh-m-glow{animation:dhMsGlow 1.1s ease-out 1}' +
  '@media (prefers-reduced-motion:reduce){.dh-m-glow{animation:none}}';

function installStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID;
  st.textContent = CSS;
  document.head.appendChild(st);
}

export class MasteryPanel {
  /** 내부 편집 상태가 바뀌면 오른다(RpgUi 재그리기 키) */
  version = 0;
  /** 설계 px 본문 안쪽 폭(RpgUi가 그릴 때마다 넣는다) */
  bodyW = 560;
  /** 대장간 안 일반/초월 전환 */
  forgeMode: 'normal' | 'transcend' = 'normal';
  private draft: MasteryAlloc | null = null;
  private draftBase = '';
  private confirm = false;
  private baseSeq = -1;
  private seenLevel = -1;
  private seenSeq = -1;
  /** 이번 그리기에서 연출할 것 */
  private fxLevel = false;
  private fxClaim = -1;

  constructor(private readonly send: (c: GameCommand) => void) { installStyle(); }

  /** RpgUi가 창을 열 때 */
  open(): void { this.confirm = false; }

  /** 다시 그리기 키: 숙련 보기 revision과 편집 상태 */
  key(view: GameView): number {
    const m = view.mastery;
    return m ? m.revision * 1000 + (m.last.seq % 1000) : -1;
  }

  private ensureBase(view: GameView): void {
    const m = view.mastery;
    if (!m) return;
    if (this.baseSeq < 0) { this.baseSeq = m.last.seq; this.seenLevel = m.level; this.seenSeq = m.last.seq; }
    // 장부 배분이 바뀌면(적용·다른 탭) 편집을 버린다
    const k = m.alloc.join(',');
    if (this.draft && k !== this.draftBase) { this.draft = null; this.confirm = false; }
  }

  /** 레벨업·이정표·적용 연출 판정(그릴 때 한 번) */
  private detectFx(view: GameView): void {
    const m = view.mastery;
    this.fxLevel = false; this.fxClaim = -1;
    if (!m) return;
    if (m.level > this.seenLevel && this.seenLevel >= 0) { this.fxLevel = true; uiSfx('levelUp'); }
    this.seenLevel = m.level;
    if (m.last.seq !== this.seenSeq) {
      this.seenSeq = m.last.seq;
      if (m.last.kind === 'claim' && m.last.code === 'ok') uiSfx('skillLearn');
      else if (m.last.kind === 'alloc' && m.last.code === 'ok') uiSfx('uiConfirm');
    }
  }

  private btn(parent: HTMLElement, label: string, onClick: () => void, o: { sq?: boolean; pri?: boolean; disabled?: boolean; testid?: string; aria?: string } = {}): HTMLButtonElement {
    const b = el('button', '', parent, label);
    b.type = 'button';
    b.className = 'dh-m-btn' + (o.sq ? ' sq' : '') + (o.pri ? ' pri' : '');
    b.disabled = !!o.disabled;
    if (o.testid) b.dataset.testid = o.testid;
    if (o.aria) b.setAttribute('aria-label', o.aria);
    b.addEventListener('click', () => { if (!b.disabled) onClick(); });
    return b;
  }

  private setDraft(view: GameView, next: MasteryAlloc | null): void {
    const m = view.mastery;
    if (!m || !next) return;
    this.draft = next; this.draftBase = m.alloc.join(','); this.confirm = false; this.version++;
  }

  /** 캐릭터 창 「숙련」 탭 본문 */
  render(body: HTMLElement, view: GameView, locked: boolean): void {
    this.ensureBase(view);
    this.detectFx(view);
    const mv = view.mastery;
    const usable = !!mv && !locked && view.gear.mode === 'ok';
    const m = masteryModel(mv, this.draft, this.confirm, view.profile.gold, usable);
    const root = el('div', '', body);
    root.className = 'dh-m';
    root.dataset.testid = 'mastery-panel';
    if (!m.ready) { el('p', 'margin:0;color:#bdcce0;font:600 13px ' + FONT + ';', root, m.emptyHint); return; }

    // 레벨·진행
    const head = el('div', '', root);
    head.className = 'dh-m-card' + (this.fxLevel && !reducedMotion() ? ' dh-m-glow' : '');
    head.dataset.testid = 'mastery-head';
    const top = el('div', 'display:flex;align-items:baseline;gap:10px;', head);
    el('span', 'font:900 15px ' + FONT + ';color:#ffd76a;', top, '숙련');
    const lv = el('span', '', top, String(m.level)); lv.className = 'dh-m-lv'; lv.dataset.testid = 'mastery-level';
    if (this.fxLevel) el('b', 'margin-left:auto;font:900 13px ' + FONT + ';color:#bfeaff;', top, `숙련 ${m.level} 달성!`).dataset.testid = 'mastery-levelup';
    const bar = el('div', '', head);
    bar.className = 'dh-m-bar'; bar.dataset.testid = 'mastery-bar';
    bar.setAttribute('role', 'progressbar'); bar.setAttribute('aria-valuemin', '0'); bar.setAttribute('aria-valuemax', '100'); bar.setAttribute('aria-valuenow', String(Math.round(m.ratio * 100)));
    bar.setAttribute('aria-label', '숙련 진행');
    el('span', 'width:' + Math.round(m.ratio * 100) + '%', bar);
    el('div', '', head, m.progressText).className = 'dh-m-note';
    (head.lastElementChild as HTMLElement).dataset.testid = 'mastery-progress';
    const pts = el('div', 'font:900 14px ' + FONT + ';color:#fff;', head, m.pointsText);
    pts.dataset.testid = 'mastery-points';
    if (m.emptyHint) { const h = el('div', '', head, m.emptyHint); h.className = 'dh-m-warn'; h.dataset.testid = 'mastery-empty'; }

    // 4칸 배분
    const L = statRowLayout(this.bodyW);
    const draftNow: MasteryAlloc = this.draft ?? (mv as NonNullable<typeof mv>).alloc;
    for (const r of m.rows) {
      const row = el('div', `grid-template-columns:minmax(0,1fr) ${BTN}px ${L.val.w}px ${BTN}px;`, root);
      row.className = 'dh-m-row'; row.dataset.testid = 'mastery-' + r.stat;
      const copy = el('div', 'min-width:0;', row);
      el('b', '', copy, `${r.name} ${r.effect}`).dataset.testid = 'mastery-' + r.stat + '-effect';
      el('small', '', copy, r.per);
      this.btn(row, '−', () => this.setDraft(view, draftStep(draftNow, mv!.points, r.idx, -1)), { sq: true, disabled: !r.canDec, testid: 'mastery-' + r.stat + '-dec', aria: r.name + ' 포인트 빼기' });
      const val = el('span', '', row, String(r.value)); val.className = 'dh-m-val'; val.dataset.testid = 'mastery-' + r.stat + '-value';
      this.btn(row, '+', () => this.setDraft(view, draftStep(draftNow, mv!.points, r.idx, 1)), { sq: true, disabled: !r.canInc, testid: 'mastery-' + r.stat + '-inc', aria: r.name + ' 포인트 더하기' });
    }
    // 적용·확인
    if (m.confirming) {
      const box = el('div', '', root);
      box.className = 'dh-m-confirm'; box.dataset.testid = 'mastery-confirm';
      box.setAttribute('role', 'alertdialog');
      el('div', 'font:800 13px ' + FONT + ';color:#ffe7a0;line-height:1.45;', box, m.confirmText).dataset.testid = 'mastery-confirm-text';
      const act = el('div', 'display:flex;gap:8px;flex-wrap:wrap;', box);
      this.btn(act, '네, 재분배', () => {
        this.send({ type: 'masteryAlloc', alloc: draftNow, requestId: makeRequestId() });
        this.confirm = false; this.version++;
      }, { pri: true, disabled: !m.affordable || !usable, testid: 'mastery-confirm-yes' });
      this.btn(act, '취소', () => { this.confirm = false; this.version++; }, { testid: 'mastery-confirm-no' });
    } else {
      const act = el('div', 'display:flex;gap:8px;flex-wrap:wrap;', root);
      this.btn(act, m.applyLabel, () => {
        if (m.respec) { this.confirm = true; this.version++; return; }
        this.send({ type: 'masteryAlloc', alloc: draftNow, requestId: makeRequestId() });
      }, { pri: true, disabled: m.applyDisabled, testid: 'mastery-apply' });
      this.btn(act, '되돌리기', () => { this.draft = null; this.confirm = false; this.version++; }, { disabled: !m.dirty, testid: 'mastery-reset' });
      if (m.dirty && m.respec) el('span', 'align-self:center;font:700 12px ' + FONT + ';color:#ffb37a;', act, `줄이면 재분배 비용 ${m.cost.toLocaleString('ko-KR')}골드`).dataset.testid = 'mastery-respec-hint';
      else if (m.dirty) el('span', 'align-self:center;font:700 12px ' + FONT + ';color:#abbcd3;', act, '늘리기만 하면 무료예요');
    }
    const msg = masteryMessage(mv?.last, this.baseSeq);
    if (msg) { const p = el('p', 'margin:0;font:800 12px ' + FONT + ';color:' + TONE[msg.tone] + ';', root, msg.text); p.setAttribute('role', 'status'); p.dataset.testid = 'mastery-msg'; p.dataset.tone = msg.tone; }

    // 초월 해금 안내
    const uh = el('div', '', root);
    uh.className = 'dh-m-h'; el('span', '', uh, '초월 강화 해금'); el('small', '', uh, '+15 위로 +16~+20');
    const un = el('div', '', root);
    un.className = 'dh-m-un'; un.dataset.testid = 'mastery-unlocks';
    for (const u of m.unlocks) { const s = el('span', '', un, (u.ok ? '✔ ' : '') + u.label); if (u.ok) s.className = 'ok'; s.dataset.testid = 'mastery-unlock-' + u.to; }

    // 이정표
    const mh = el('div', '', root);
    mh.className = 'dh-m-h'; el('span', '', mh, '숙련 이정표'); el('small', '', mh, m.readyCount > 0 ? `받을 수 있는 보상 ${m.readyCount}` : '숙련을 올리면 열려요');
    const list = el('div', 'display:flex;flex-direction:column;gap:5px;', root);
    list.dataset.testid = 'mastery-milestones';
    for (const ms of m.milestones) {
      const row = el('div', '', list);
      row.className = 'dh-m-ms ' + ms.state + (this.fxClaim === ms.idx ? ' dh-m-glow' : '');
      row.dataset.testid = 'mastery-ms-' + ms.idx; row.dataset.state = ms.state;
      const copy = el('div', 'min-width:0;', row);
      el('b', '', copy, `숙련 ${ms.at}${ms.note ? ' · ' + ms.note : ''}`);
      el('small', '', copy, ms.reward);
      this.btn(row, ms.label, () => this.send({ type: 'masteryClaim', idx: ms.idx, requestId: makeRequestId() }), { pri: ms.state === 'ready', disabled: ms.state !== 'ready' || !usable, testid: 'mastery-claim-' + ms.idx });
    }
  }

  /** 대장간 「강화」 탭 위쪽 일반/초월 전환 줄 */
  renderModeToggle(parent: HTMLElement, view: GameView, onChange: () => void): void {
    const tm = transcendModel(view.mastery, view.gear.stones, view.profile.gold, true);
    const seg = el('div', '', parent);
    seg.className = 'dh-m-seg'; seg.dataset.testid = 'forge-mode';
    const mk = (mode: 'normal' | 'transcend', label: string): void => {
      const b = this.btn(seg, label, () => { this.forgeMode = mode; this.version++; onChange(); }, { testid: 'forge-mode-' + mode });
      b.setAttribute('aria-pressed', String(this.forgeMode === mode));
    };
    mk('normal', '일반 강화 (+1~+15)');
    mk('transcend', '초월 강화 (+16~+20)' + (tm.tryable > 0 ? ' ●' : ''));
  }

  /** 대장간 초월 구역: 6부위 카드 */
  renderTranscend(body: HTMLElement, view: GameView, locked: boolean): void {
    this.ensureBase(view);
    const usable = !locked && view.gear.mode === 'ok';
    const tm = transcendModel(view.mastery, view.gear.stones, view.profile.gold, usable);
    const root = el('div', '', body);
    root.className = 'dh-m';
    root.dataset.testid = 'transcend-section';
    const h = el('div', '', root);
    h.className = 'dh-m-h'; el('span', '', h, '초월 강화'); el('small', '', h, '+15 장비를 낀 부위만');
    el('div', '', root, tm.rule || TRANSCEND_RULE).className = 'dh-m-note';
    if (!tm.ready) { el('p', 'margin:0;color:#bdcce0;font:600 13px ' + FONT + ';', root, '숙련 정보를 불러오는 중이에요.'); return; }
    const L = transcendLayout(this.bodyW, tm.cards.length);
    const grid = el('div', `grid-template-columns:repeat(${L.cols},minmax(0,1fr));`, root);
    grid.className = 'dh-m-grid'; grid.dataset.testid = 'transcend-grid';
    for (const c of tm.cards) {
      const card = el('div', '', grid);
      card.className = 'dh-m-tc' + (c.step > 0 ? ' s' + c.step : '');
      card.dataset.testid = 'transcend-card-' + c.slot;
      card.dataset.slot = c.slot; card.dataset.step = String(c.step);
      const nm = el('div', '', card); nm.className = 'nm';
      el('span', '', nm, c.slotName);
      el('i', '', nm, '초월 ' + c.nowLabel).dataset.testid = 'transcend-now-' + c.slot;
      if (c.maxed) { el('div', '', card, '최대 초월에 닿았어요').className = 'dh-m-note'; }
      else {
        const ch = el('div', '', card, c.chanceText); ch.className = 'ch'; ch.dataset.testid = 'transcend-chance-' + c.slot;
        el('div', '', card, `+${c.to} 성공 확률 · 기본 ${c.baseText} · ${c.pityText}`).className = 'dh-m-note';
        el('div', '', card, c.failsText).className = 'dh-m-note';
        el('div', '', card, c.stonesText).className = 'dh-m-note';
        el('div', '', card, c.goldText).className = 'dh-m-note';
      }
      el('div', '', card, c.effectText).className = 'dh-m-note';
      if (c.lock) { const lk = el('div', '', card, c.lock); lk.className = 'dh-m-warn'; lk.dataset.testid = 'transcend-lock-' + c.slot; lk.dataset.kind = c.lockKind; }
      const b = this.btn(card, c.tryLabel, () => this.send({ type: 'transcend', slot: c.slot as EquipSlot, requestId: makeRequestId() }), { pri: true, disabled: !c.canTry, testid: 'transcend-try-' + c.slot });
      b.style.width = '100%'; b.style.minHeight = 'max(' + L.btnH + 'px,var(--dh-btn-min,' + BTN + 'px))'; b.style.marginTop = '4px';
    }
    const msg = transcendMessage(view.mastery?.last, this.baseSeq);
    if (msg) { const p = el('p', 'margin:0;font:800 13px ' + FONT + ';color:' + TONE[msg.tone] + ';', root, msg.text); p.setAttribute('role', 'status'); p.dataset.testid = 'transcend-msg'; p.dataset.tone = msg.tone; }
  }
}
